// PRFAQSection.js - Portugal
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

const PRFAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'What is the minimum investment for Portugal Golden Visa?',
      answer: 'The minimum investment is €250,000 for cultural heritage donations or €500,000 for capital transfer, investment funds, or scientific research. Job creation requires 10 full-time positions. Note that real estate investment options in coastal areas have been phased out, with focus now on interior regions and investment funds.'
    },
    {
      question: 'How much time do I need to spend in Portugal?',
      answer: 'Golden Visa requires only 7 days per year on average (14 days over 2 years). D7 Visa requires 16 months over the first 2-year period. For permanent residency and citizenship, you must demonstrate genuine ties to Portugal and meet minimum stay requirements.'
    },
    {
      question: 'Can I work in Portugal with a Golden Visa?',
      answer: 'Yes, Golden Visa holders have the right to work in Portugal and throughout the EU. However, the Golden Visa is primarily designed for investors who do not need Portuguese employment. D7 Visa holders can also work, though it\'s designed for those with passive income.'
    },
    {
      question: 'What is the D7 Visa income requirement?',
      answer: 'D7 Visa requires proof of passive income equivalent to Portuguese minimum wage: €820/month for main applicant, plus 50% (€410) for spouse, and 30% (€246) per dependent child. Income must be stable and recurring from pensions, rental income, investments, or remote work.'
    },
    {
      question: 'How long until I can apply for citizenship?',
      answer: 'You can apply for Portuguese citizenship after 5 years of legal residency. Requirements include: basic Portuguese language (A2 level), clean criminal record, ties to Portugal, and meeting minimum stay requirements. Portugal allows dual citizenship.'
    },
    {
      question: 'Can I include my family in the application?',
      answer: 'Yes, both Golden Visa and D7 Visa allow family reunification. You can include your spouse/partner, dependent children (under 18 or under 26 if students), and dependent parents. Each family member requires additional government fees but can be included in the same application.'
    },
    {
      question: 'What is the Non-Habitual Resident (NHR) tax regime?',
      answer: 'NHR is a special tax regime offering significant tax benefits for 10 years. Foreign-source income (pensions, dividends, rental income) may be tax-exempt or taxed at reduced rates. Portuguese-source income from high-value activities is taxed at flat 20%. You must not have been a Portuguese tax resident in the previous 5 years to qualify.'
    },
    {
      question: 'Do I need to speak Portuguese?',
      answer: 'Portuguese language is not required for initial residency (Golden Visa or D7). However, A2 level Portuguese is required for citizenship application after 5 years. Many Portuguese speak English, especially in Lisbon and Porto. Language courses are widely available.'
    },
    {
      question: 'What happens to my investment after 5 years?',
      answer: 'For Golden Visa, you must maintain your investment for minimum 5 years. After obtaining permanent residency or citizenship, you can liquidate your investment. However, many investors choose to maintain their investments as Portugal offers attractive returns and lifestyle benefits.'
    },
    {
      question: 'What is the success rate for Portuguese residency applications?',
      answer: 'Success rates are very high for complete applications meeting all requirements. Golden Visa applications with proper investment documentation typically have 90-95% approval rates. D7 Visa applications with sufficient income proof have similar success rates. Our clients who follow our guidance have a 98% success rate.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-600 text-base font-light">
            Common questions about Portuguese residency applications
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className="bg-[#F5F8FA] rounded-lg shadow-sm overflow-hidden border border-gray-200"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
              >
                <span className="text-base font-semibold text-[#202356] pr-4">
                  {faq.question}
                </span>
                <div className="flex-shrink-0">
                  {openIndex === index ? (
                    <Minus size={24} className="text-[#2542D4]" />
                  ) : (
                    <Plus size={24} className="text-[#2542D4]" />
                  )}
                </div>
              </button>

              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 text-gray-600 text-sm font-light leading-relaxed border-t border-gray-200 pt-4">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6 text-center"
        >
          <p className="text-gray-700 text-sm font-light leading-relaxed">
            <strong>Still have questions?</strong> Book a free consultation with our immigration experts to discuss your specific situation and get personalized guidance on your Portuguese residency journey.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PRFAQSection;
